import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  authService,
  userService,
  normalizeLoginResponse,
  formatApiError,
  formatLoginError,
  AUTH_EXPIRED_EVENT,
} from "../services/api";

const AuthContext = createContext(null);

/** Evita spam de GET profile al cambiar de pestaña varias veces. */
const PROFILE_REFRESH_DEBOUNCE_MS = 3000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const lastProfileFetchAt = useRef(0);

  const mergeProfileIntoUser = useCallback((profileData) => {
    if (!profileData) return;
    setProfile(profileData);
    setUser((prev) => {
      // En F5/boot, el fetch puede resolver antes de que React aplique setUser(savedUser).
      // Si prev es null, usamos sessionStorage o el propio profile como base.
      const base =
        prev ||
        authService.getCurrentUser() ||
        (profileData.id ? profileData : null);
      if (!base) return prev;
      const merged = {
        ...base,
        ...profileData,
        balance: profileData.balance ?? base.balance,
        profile_photo_url:
          profileData.profile_photo_url ||
          profileData.profile_photo ||
          base.profile_photo_url,
      };
      sessionStorage.setItem("user", JSON.stringify(merged));
      return merged;
    });
  }, []);

  /**
   * Fuente de verdad del saldo: GET /users/{id}/profile/
   * @param {string} [overrideUserId]
   */
  const loadProfile = useCallback(
    async (overrideUserId) => {
      const id =
        overrideUserId ||
        user?.id ||
        authService.getCurrentUser()?.id;
      if (!id) return null;

      try {
        const result = await userService.getProfile(id);
        if (result.ok && result.data) {
          const profileData =
            result.data?.user || result.data?.data || result.data;
          mergeProfileIntoUser(profileData);
          lastProfileFetchAt.current = Date.now();
          return profileData;
        }
      } catch (error) {
        console.error("Load profile error:", error);
      }
      return null;
    },
    [user?.id, mergeProfileIntoUser]
  );

  const refreshProfileIfDue = useCallback(() => {
    if (!authService.isAuthenticated()) return;
    const now = Date.now();
    if (now - lastProfileFetchAt.current < PROFILE_REFRESH_DEBOUNCE_MS) return;
    loadProfile();
  }, [loadProfile]);

  // Restaurar sesión + refrescar perfil desde red
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      const savedUser = authService.getCurrentUser();
      if (savedUser && authService.isAuthenticated()) {
        setUser(savedUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        if (!cancelled && savedUser.id) {
          await loadProfile(savedUser.id);
        }
      } else {
        setIsLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
    // Solo al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setProfile(null);
      setIsAuthenticated(false);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // Al volver a la pestaña / focus: sincronizar saldo con el backend
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshProfileIfDue();
      }
    };
    const onFocus = () => {
      refreshProfileIfDue();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, refreshProfileIfDue]);

  // Login
  const login = async (phoneNumber, pin) => {
    setIsLoading(true);
    try {
      const result = await authService.login(phoneNumber, pin);
      const norm = result.normalized || normalizeLoginResponse(result.data || {});

      if (result.ok && norm.access) {
        const u = norm.user || authService.getCurrentUser();
        setUser(u);
        setIsAuthenticated(true);
        if (u?.id) {
          await loadProfile(u.id);
        }
        return { success: true, user: u };
      }

      if (result.ok && !norm.access && norm.user) {
        setUser(norm.user);
        setIsAuthenticated(authService.isAuthenticated());
        if (norm.user?.id) {
          await loadProfile(norm.user.id);
        }
        return { success: true, user: norm.user };
      }

      return {
        success: false,
        error: formatLoginError(result.data),
      };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Error de conexión" };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    lastProfileFetchAt.current = 0;
  }, []);

  // Registro
  const register = async (userData) => {
    setIsLoading(true);
    try {
      const result = await userService.register(userData);

      if (result.ok) {
        return { success: true, user: result.data };
      }
      return {
        success: false,
        error: formatApiError(result.data) || "No fue posible completar el registro",
      };
    } catch (error) {
      console.error("Register error:", error);
      return { success: false, error: "Error de conexión" };
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizar perfil
  const updateProfile = async (userData) => {
    if (!user?.id) return { success: false, error: "No autenticado" };

    try {
      const result = await userService.updateProfile(user.id, userData);

      if (result.ok) {
        const nextUser = result.data.user || result.data;
        if (nextUser?.id) setUser(nextUser);
        await loadProfile(user.id);
        return { success: true, user: nextUser };
      } else {
        return {
          success: false,
          error: formatApiError(result.data) || "Error al actualizar",
        };
      }
    } catch (error) {
      console.error("Update profile error:", error);
      return { success: false, error: "Error de conexión" };
    }
  };

  /** Actualizar perfil con FormData (foto). */
  const updateProfileMultipart = async (formData) => {
    if (!user?.id) return { success: false, error: "No autenticado" };
    try {
      const result = await userService.updateProfileMultipart(user.id, formData);
      if (result.ok) {
        if (result.user?.id) setUser(result.user);
        await loadProfile(user.id);
        return { success: true, user: result.user };
      }
      return {
        success: false,
        error: formatApiError(result.data) || "Error al actualizar",
      };
    } catch (error) {
      console.error("Update profile multipart error:", error);
      return { success: false, error: "Error de conexión" };
    }
  };

  // Actualizar balance localmente (después de compras); preferir loadProfile para sync real
  const updateBalance = (newBalance) => {
    if (user) {
      const updatedUser = {
        ...user,
        balance: { ...user.balance, amount: newBalance },
      };
      setUser(updatedUser);
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    profile,
    isLoading,
    isAuthenticated,
    login,
    logout,
    register,
    loadProfile,
    updateProfile,
    updateProfileMultipart,
    updateBalance,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return context;
};
